// pwa.js — register service worker (PWA + push handling + AUTOMATIC updates)
// Strategy: any time a new SW is detected, activate it immediately and reload.
// No banner needed in the common case. We persist a flag in localStorage so the
// next page-load can show "✓ updated to latest version" toast for confirmation.
//
// Force-update API (`forceFullReset`) is exposed for the "تحديث التطبيق" button
// in the user menu — unregister SW, drop all caches, hard-reload.

const JUST_UPDATED_KEY = "thawany:just-updated";

export function registerServiceWorker(onUpdateReady) {
  if (!("serviceWorker" in navigator)) return Promise.resolve(null);

  // Reload exactly once when the new SW takes control.
  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) return;
    // v144 — لا تعد التحميل أثناء العودة من دخول Google (يجهض تبادل الجلسة)
    if ((window.location.hash || "").includes("session_id=")) return;
    refreshing = true;
    try { localStorage.setItem(JUST_UPDATED_KEY, "1"); } catch { /* private mode etc. */ }
    window.location.reload();
  });

  return navigator.serviceWorker.register("/sw.js", { scope: "/" })
    .then(async (reg) => {
      // eslint-disable-next-line no-console
      console.log("[pwa] sw registered:", reg.scope);

      // Auto-activate any worker already waiting from a previous session.
      if (reg.waiting && navigator.serviceWorker.controller) {
        reg.waiting.postMessage({ type: "SKIP_WAITING" });
        // The banner is still optionally available as a non-blocking notice.
        onUpdateReady && onUpdateReady(reg);
      }

      // When a new SW finishes installing while the page is open: auto-apply.
      reg.addEventListener("updatefound", () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            newWorker.postMessage({ type: "SKIP_WAITING" });
            // Notify the UI so it can pre-emptively show a "updating…" hint.
            onUpdateReady && onUpdateReady(reg);
          }
        });
      });

      // Force an update check on every page load (cheap — HTTP cache headers do the heavy lifting).
      reg.update().catch(() => {});
      // Plus a periodic check every 15 min for users who keep the tab open all day.
      setInterval(() => { reg.update().catch(() => {}); }, 15 * 60 * 1000);
      return reg;
    })
    .catch((e) => {
      console.warn("[pwa] sw register failed:", e);
      return null;
    });
}

/** Pulled at app boot. Returns true once if the previous load triggered an update. */
export function consumeJustUpdatedFlag() {
  try {
    const flag = localStorage.getItem(JUST_UPDATED_KEY);
    if (flag) {
      localStorage.removeItem(JUST_UPDATED_KEY);
      return true;
    }
  } catch { /* ignore */ }
  return false;
}

/** Activate the waiting SW. Kept for backwards-compat (UpdateBanner button). */
export function activateWaitingWorker() {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.getRegistration().then((reg) => {
    if (reg && reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
  });
}

/** Hard reset — drop all caches and unregister the SW, then hard-reload.
 * Used by the "تحديث التطبيق" button when the user wants to be 100% sure.
 */
export async function forceFullReset() {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    try { localStorage.setItem(JUST_UPDATED_KEY, "1"); } catch { /* ignore */ }
  } catch (e) {
    console.warn("[pwa] forceFullReset failed:", e);
  }
  // Bust HTTP cache too.
  const url = new URL(window.location.href);
  url.searchParams.set("_v", String(Date.now()));
  window.location.replace(url.toString());
}

export function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function subscribeToPush(api) {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { ok: false, reason: "غير مدعوم في هذا المتصفح" };
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, reason: "تم رفض إذن الإشعارات" };
  }
  const { data: keyData } = await api.get("/push/public-key");
  if (!keyData?.public_key) return { ok: false, reason: "VAPID غير مهيّأ" };
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) await existing.unsubscribe();
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(keyData.public_key),
  });
  const subJson = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: subJson.endpoint,
    keys: subJson.keys,
  });
  return { ok: true, endpoint: subJson.endpoint };
}

export async function unsubscribeFromPush(api) {
  if (!("serviceWorker" in navigator)) return { ok: true };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
    await sub.unsubscribe();
  }
  return { ok: true };
}

export async function pushStatus(api) {
  try { const { data } = await api.get("/push/status"); return data; }
  catch { return { subscribed: false }; }
}

export async function sendTestPush(api) {
  const { data } = await api.post("/push/test");
  return data;
}
